import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { Alert, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import commonStore from "../../Store/marco/commonStore";
import emissionViewStore from "../../Store/marco/emissionViewStore";
import { companiesForYear } from "../../OrganizationalStructure/utils/company-operating-status";

export const InventoryCompanyGuard = observer(({ children }) => {
  const { t } = useTranslation("OrgStructure");
  const [loading, setLoading] = useState(commonStore.scopeType === "COMPANY");
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState(false);
  const [reload, setReload] = useState(0);
  const store = emissionViewStore;
  const mode = commonStore.scopeType;
  const userId = commonStore.userId;
  const companies = commonStore.companyList || [];
  const available = companiesForYear(companies, store.selectedYear, mode);
  const requiresEntity = !["Year", "Entity"].includes(store.selectedView);
  const blocked = !loading && !error && mode === "COMPANY" && Boolean(store.selectedYear) &&
    (store.currentCId
      ? !available.some(c => String(c.id) === String(store.currentCId))
      : requiresEntity);

  useEffect(() => {
    if (mode !== "COMPANY") { setLoading(false); setError(false); return undefined; }
    let active = true;
    setLoading(true);
    setError(false);
    commonStore._handleSetCompanyList(userId)
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [mode, userId, reload]);

  useEffect(() => {
    if (!blocked) return;
    runInAction(() => {
      store._handleSetCurrentCId("");
      store._handleSetCurrentCompanyName("");
      store._handleSetAuthComArr([]);
      store.selectedDataEntry = null;
      store.selectedDataEntryItem = null;
      ["showEmissionActivityModal", "showEmissionFactorEditModal", "isShowEFIncertModal",
        "showDataEntryAddModal", "showDataEntryEditModal", "showDataEntryDeleteModal",
        "showDataEntrySetUnitModal", "showDataEntryUploadFilesModal", "showDataEntryPreviewModal",
        "showAddCustomEFModal", "isShowDataEntryUnitMismatchModal",
        "isShowDataEntryAddAndSetUnitModal"].forEach(key => { store[key] = false; });
      if (store.selectedView !== "Year") store._handleSetSelectedView("Entity");
    });
    setNotice(true);
  }, [blocked, store]);

  // Do not mount editable screens, their request effects or modals while blocked.
  if (loading) return <CircularProgress aria-label={t("Loading companies")} />;
  if (error) return <Alert severity="error" action={<Button onClick={() => setReload(n => n + 1)}>{t("Retry")}</Button>}>
    {t("Unable to load companies")}
  </Alert>;
  if (blocked) return null;
  return <>
    {notice && <Alert severity="info" onClose={() => setNotice(false)}>
      {t("The selected entity is unavailable for this year. Please select another entity.")}
    </Alert>}
    {children}
  </>;
});
